import { Button, Grid, Paper, Typography } from '@mui/material';
import { exportPatients, getAvailablePatientFields, getProjectsFieldTemplates } from 'api';
import useRequest from 'hooks/useRequest';
import PatientFilterRow from './Filters/PatientFilter/PatientFilterRow';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import AddFilterContainer from './AddFilterContainer';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { filterState } from 'state/exportState';
import useQuery from 'hooks/useQuery';
import { FieldTemplate, FormTemplateType, Patient, User } from 'api/models';
import { GeneralIds, ModelTypes } from 'types/types';
import FormFilterRow from './Filters/FormFilterRow';
import { useParams } from 'react-router-dom';
import FieldFilterRow from './Filters/FieldFilterRow';
import { Condition, ReferenceType } from 'api/models/FieldTemplate';
import ResultList from './ResultList';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { ToastSeverity, toastStateSelector } from 'state';
import { getExportValidationSchema } from './utils';
import i18next from 'i18next';

export default function Export() {
  const { data: patientFieldsResponse } = useRequest<string[]>(
    getAvailablePatientFields,
  );
  const { t } = useTranslation();
  const [filteringLoading, setFilteringLoading] = useState(false);
  const setToast = useSetRecoilState(toastStateSelector);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [count, setCount] = useState();
  const [filters, setFilters] = useRecoilState(filterState);
  const { projectId } = useParams<GeneralIds>();

  useEffect(() => {
    setFilters([]);
  }, [setFilters]);

  const { data: formTemplates, loading: formTemplatesLoading } = useQuery<
    FormTemplateType[]
  >({
    type: ModelTypes.formTemplateType,
    ids: {
      projectId,
    },
  });

  const { data: fields, loading: fieldTemplatesLoading } = useRequest<
    FieldTemplate[]
  >(getProjectsFieldTemplates, {}, [{ projectId, publishedOnly: true }]);

  const { data: users, loading: usersLoading } = useQuery<User[]>({
    type: ModelTypes.user,
    ids: {
      projectId,
    },
  });

  const loading = formTemplatesLoading || fieldTemplatesLoading || usersLoading;

  const formik = useFormik<{ filters: Condition[] }>({
    initialValues: { filters: [] },
    validationSchema: getExportValidationSchema(t),
    onSubmit: async (values, { setSubmitting }) => {
      setFilteringLoading(true);
      try {
        const resp = await exportPatients({
          projectId,
          patientFilters: values.filters,
        });
        // TODO: fix types
        const patients = resp.data.map((el: any) => {
          return {
            ...el,
            ...el?.attributes,
          };
        }) as Patient[];
        setPatients(patients);
        setCount(resp.meta.totalCount);
      } catch (e: any) {

        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18next.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.export'),
          severity: ToastSeverity.error,
        });
      } finally {
        setFilteringLoading(false);
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    formik.setFieldValue('filters', filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Box>
        <AddFilterContainer loading={loading} />
        {filters.length > 0 && (
          <Typography variant="h6" mb={2} mt={2}>
            {t('export.filterCriteria')}
          </Typography>
        )}
        <Grid container rowGap={2}>
          {filters.map((filter, index) => {
            switch (filter.reference.type) {
              case ReferenceType.patientFieldReference:
                return (
                  <PatientFilterRow
                    key={`${filter.type}-${index}-patient-filter`}
                    availablePatientFields={patientFieldsResponse}
                    index={index}
                  />
                );
              case ReferenceType.formReference:
                return (
                  <FormFilterRow
                    key={`${filter.type}-${index}-form-filter`}
                    formTemplates={formTemplates}
                    index={index}
                    users={users}
                  />
                );
              case ReferenceType.formFieldReference:
                return (
                  <FieldFilterRow
                    key={`${filter.type}-${index}-field-filter`}
                    formTemplates={formTemplates}
                    index={index}
                    fields={fields}
                  />
                );
              default:
                return null;
            }
          })}
        </Grid>
        {filters.length > 0 &&
          <>
            <Box mt={2} display={'flex'} justifyContent={'end'} alignItems={'end'}>
              <Box>
                <Button
                  variant="contained"
                  onClick={formik.submitForm}
                  disabled={filteringLoading || !formik.isValid}
                >
                  {`${t('export.filter')} ${filteringLoading ? '...' : ''}`}
                </Button>
              </Box>
            </Box>
            <ResultList
              filters={filters}
              patients={patients}
              count={count}
              fields={fields}
              patientFields={patientFieldsResponse}
              formTemplates={formTemplates}
            />
          </>
        }
      </Box>
    </Paper>
  );
}
